"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import withApplication from "@/components/with-application";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { PRODUCT_DOMAIN } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { MakeClaimContentProps } from "@/types/confirmation-types";

function MakeClaimContent({
  markingCliamsTitle,
  claimsHotlineText,
  claimsHotlineNumber,
  endSupplierFailureText,
  endSupplierFailureNumber,
  claimsHotlineEmailText,
  claimsHotlineEmailAddress,
  legalCostText,
  legalCostNumber,
  defaultExpanded = false,
  nonEmergencyClaims,
  medicalAssistanceInEurope,
  medicalAssistanceWithinTheUSAandCanada,
}: Readonly<MakeClaimContentProps>) {
  const [makeClaimAccordionOpen, setMakeClaimAccordionOpen] = useState(true);
  const { current } = useWizard();
  function toggleMakeClaimAccordion() {
    setMakeClaimAccordionOpen(!makeClaimAccordionOpen);
  }

  const isMobile = useMediaQuery("(max-width: 599px)");

  let primaryContactNumber = claimsHotlineNumber;

  let phoneNumBtnStyle =
    "focus:outline-offset-4 focus:outline focus:outline-interactive-focus-default rounded-radius-pill md:mr-2 ml-2 md:ml-0";

  let textStyles =
    "text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold";

  /*
  TODO: Remove this logic with PRODUCT_DOMAIN once this component is refactored .
  */

  if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
    const regExp = /(?<=\+)\d+(?:\s\d+)*/;
    const match = regExp.exec(claimsHotlineNumber);
    if (match) {
      const [firstMatch] = match;
      primaryContactNumber = `+${firstMatch}`;
    }

    phoneNumBtnStyle += "px-[0]";
    textStyles =
      "text-start text-[18px] font-bold leading-[23px] md:leading-[28px] lg:leading-[23px]";
  }

  const contactDetailsLinesStyle = useMemo(() => {
    if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
      return "md:flex-col md:items-start";
    }

    return "md:flex-row md:justify-between";
  }, []);

  const maxWidthStyle = useMemo(() => {
    if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
      return "max-w-[100%]";
    }
    return "max-w-[197px]";
  }, []);

  useEffect(() => {
    if (isMobile) {
      setMakeClaimAccordionOpen(defaultExpanded);
    } else {
      setMakeClaimAccordionOpen(true);
    }
  }, [isMobile, defaultExpanded]);

  const handleCall = (number: string) => {
    const numberToCall = /\d+/.exec(number.split(" ").join(""));
    const telUri = `tel:${numberToCall?.[0]}`;
    window.open(telUri, "_self");
  };

  const handleEmailClick = (email: string) => {
    const mailtoUri = `mailto:${email}`;
    window.open(mailtoUri, "_self");
  };

  return (
    <div
      className=" flex flex-col gap-spacing-s md:gap-spacing-m lg:gap-spacing-l"
      data-testid="makeClaimContent-testid"
    >
      <button
        id="make-claim-accordion-button"
        type="button"
        className="transition duration-300 w-full flex flex-row justify-between items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-interactive-focus-default"
        onClick={toggleMakeClaimAccordion}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            toggleMakeClaimAccordion();
          }
        }}
      >
        <h1 className="w-[calc(100%-1rem)] text-left justify-between font-header-font text-text-title text-[23px] md:text-[31px] leading-[27.6px] md:leading-[34.1px] cursor-pointer">
          {markingCliamsTitle}
        </h1>
        {makeClaimAccordionOpen ? (
          <ChevronUpIcon
            stroke={selectedColorBrand["interactive-secondary-default"]}
          />
        ) : (
          <ChevronDownIcon
            stroke={selectedColorBrand["interactive-secondary-default"]}
          />
        )}
      </button>
      <div
        className={`space-y-spacing-s md:space-y-spacing-m lg:space-y-spacing-l overflow-hidden transition-opacity duration-500 md:ps-0 pt-2 pb-2 ${
          makeClaimAccordionOpen ? "opacity-100" : "opacity-0 hidden"
        }`}
      >
        <div
          className={`space-y-spacing-xxs md:space-y-0 md:flex md:items-start ${contactDetailsLinesStyle}`}
        >
          <p
            className={`text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle ${maxWidthStyle} break-words`}
          >
            {claimsHotlineText}
          </p>
          <button
            id="claims-hotline-number-button"
            type="button"
            data-testid="call-btn-1"
            className={`${phoneNumBtnStyle} flex items-start`}
            onClick={() =>
              handleCall(
                current.uiElementControls?.isConfirmationNumberBrandImpact &&
                  nonEmergencyClaims
                  ? nonEmergencyClaims
                  : primaryContactNumber
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    nonEmergencyClaims
                    ? nonEmergencyClaims
                    : primaryContactNumber
                );
              }
            }}
          >
            <p className={`text-text-title  ${textStyles}`}>
              {current.uiElementControls?.isConfirmationNumberBrandImpact &&
              nonEmergencyClaims
                ? nonEmergencyClaims
                : claimsHotlineNumber}
            </p>
          </button>
        </div>
        {current.uiElementControls?.isEmailHotlineAddressAvailable && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex flex-col items-start">
            <p className="text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle break-words">
              {claimsHotlineEmailText}
            </p>

            <button
              id="end-supplier-failure-number-button"
              type="button"
              data-testid="call-btn-2"
              className={phoneNumBtnStyle}
              onClick={() => handleEmailClick(claimsHotlineEmailAddress || "")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCall(
                    current.uiElementControls
                      ?.isConfirmationNumberBrandImpact &&
                      medicalAssistanceInEurope
                      ? medicalAssistanceInEurope
                      : endSupplierFailureNumber
                  );
                }
              }}
            >
              <p className={`text-text-title ${textStyles}`}>
                {claimsHotlineEmailAddress}
              </p>
            </button>
          </div>
        )}
        {endSupplierFailureText && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between">
            <p className="text-xs   font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
              {endSupplierFailureText}
            </p>

            <button
              id="end-supplier-failure-number-button"
              type="button"
              data-testid="call-btn-2"
              className={phoneNumBtnStyle}
              onClick={() =>
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    medicalAssistanceInEurope
                    ? medicalAssistanceInEurope
                    : endSupplierFailureNumber
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCall(
                    current.uiElementControls
                      ?.isConfirmationNumberBrandImpact &&
                      medicalAssistanceInEurope
                      ? medicalAssistanceInEurope
                      : endSupplierFailureNumber
                  );
                }
              }}
            >
              <p className="text-text-title text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold">
                {current.uiElementControls?.isConfirmationNumberBrandImpact &&
                medicalAssistanceInEurope
                  ? medicalAssistanceInEurope
                  : endSupplierFailureNumber}
              </p>
            </button>
          </div>
        )}
        {legalCostText && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between">
            <p className="text-xs   font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
              {legalCostText}
            </p>
            <button
              id="legal-cost-number-button"
              type="button"
              data-testid="call-btn-3"
              className={phoneNumBtnStyle}
              onClick={() =>
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    medicalAssistanceWithinTheUSAandCanada
                    ? medicalAssistanceWithinTheUSAandCanada
                    : legalCostNumber
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCall(
                    current.uiElementControls
                      ?.isConfirmationNumberBrandImpact &&
                      medicalAssistanceWithinTheUSAandCanada
                      ? medicalAssistanceWithinTheUSAandCanada
                      : legalCostNumber
                  );
                }
              }}
            >
              <p className="text-text-title text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold">
                {current.uiElementControls?.isConfirmationNumberBrandImpact &&
                medicalAssistanceWithinTheUSAandCanada
                  ? medicalAssistanceWithinTheUSAandCanada
                  : legalCostNumber}
              </p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default withApplication(MakeClaimContent);
